import React, { useState, useEffect, useCallback } from 'react';
import { Button, Input, Spin, message, Modal, Radio } from 'antd';
import { PlusOutlined, CloseOutlined, UserOutlined } from '@ant-design/icons';
import { getUsers, getAdminUserById } from '../api';
import { formatAdminUserSelectLabel } from '../utils/userDisplay';

const styles = {
  wrap: {
    border: '1px solid #d9d9d9',
    borderRadius: 8,
    padding: '12px 14px',
    background: '#fafafa',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
    fontSize: 13,
    color: '#666',
  },
  empty: {
    textAlign: 'center',
    color: '#999',
    padding: '20px 0',
    fontSize: 13,
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    marginBottom: 12,
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 12px',
    background: '#fff',
    border: '1px solid #f0f0f0',
    borderRadius: 6,
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    background: '#e6f4ff',
    color: '#1677ff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  info: {
    flex: 1,
    minWidth: 0,
  },
  name: {
    fontWeight: 500,
    fontSize: 14,
    color: '#333',
    lineHeight: '20px',
  },
  sub: {
    fontSize: 12,
    color: '#888',
    lineHeight: '18px',
    marginTop: 2,
  },
  removeBtn: {
    flexShrink: 0,
    width: 28,
    height: 28,
    padding: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#999',
    border: 'none',
    background: 'transparent',
  },
  addRow: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
  },
  tip: {
    marginTop: 6,
    fontSize: 12,
    lineHeight: '18px',
  },
  tipValid: { color: '#52c41a' },
  tipInvalid: { color: '#ff4d4f' },
};

function formatAdminSubline(u) {
  const phone = String(u?.phone || '').trim() || '未留手机号';
  const real = String(u?.real_name || '').trim();
  const idPart = `ID:${u?.id}`;
  return real ? `${phone} · 姓名:${real} · ${idPart}` : `${phone} · ${idPart}`;
}

function formatAdminDisplayName(u) {
  return u?.nickname || u?.phone || `ID:${u?.id}`;
}

function formatOpenidBrief(openid) {
  const s = String(openid || '').trim();
  if (!s) return '未绑定微信';
  if (s.length <= 12) return s;
  return `${s.slice(0, 6)}…${s.slice(-4)}`;
}

function formatPickerSubline(u) {
  const openid = formatOpenidBrief(u?.openid);
  const mp = String(u?.mp_openid || '').trim();
  const mpPart = mp ? ` · 公众号:${formatOpenidBrief(mp)}` : '';
  return `OpenID: ${openid}${mpPart}`;
}

/**
 * 房源管理员编辑（卡片列表 + 手机号添加；同号多微信账号时可选择）
 * value: number[] 管理员用户 ID
 */
const HouseAdminEditor = ({ value = [], onChange, landlordId }) => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [phoneInput, setPhoneInput] = useState('');
  const [adding, setAdding] = useState(false);
  const [addTip, setAddTip] = useState('');
  const [addTipValid, setAddTipValid] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerCandidates, setPickerCandidates] = useState([]);
  const [selectedCandidateId, setSelectedCandidateId] = useState(null);

  const adminIds = (value || []).map((id) => Number(id)).filter((id) => id > 0);
  const adminIdsKey = adminIds.join(',');

  // 仅同步掉已不在 value 中的项；删除由 handleRemove 本地即时处理，不整表 loading
  useEffect(() => {
    if (adminIds.length === 0) {
      setAdmins([]);
      setLoading(false);
      return;
    }
    setAdmins((prev) => prev.filter((a) => adminIds.includes(Number(a.id))));
  }, [adminIdsKey, adminIds.length]);

  // 只为「尚未缓存详情」的 ID 补拉接口（首次打开编辑弹窗、外部回填等）
  useEffect(() => {
    if (adminIds.length === 0) return undefined;

    const missingIds = adminIds.filter(
      (id) => !admins.some((a) => Number(a.id) === id)
    );
    if (missingIds.length === 0) return undefined;

    let cancelled = false;
    if (admins.length === 0) setLoading(true);

    (async () => {
      try {
        const results = await Promise.all(
          missingIds.map(async (id) => {
            try {
              const r = await getAdminUserById(id);
              return r?.success && r.data ? r.data : null;
            } catch {
              return null;
            }
          })
        );
        if (cancelled) return;
        const fetched = results.filter(Boolean);
        setAdmins((prev) => {
          const merged = new Map(prev.map((a) => [Number(a.id), a]));
          fetched.forEach((u) => merged.set(Number(u.id), u));
          return adminIds
            .map((id) => merged.get(id))
            .filter(Boolean);
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [adminIdsKey, admins]);

  const handleRemove = useCallback(
    (admin) => {
      const uid = Number(admin?.id);
      if (!uid) return;
      const next = adminIds.filter((id) => id !== uid);
      setAdmins((prev) => prev.filter((a) => Number(a.id) !== uid));
      onChange?.(next);
      setAddTip('');
      const name = formatAdminDisplayName(admin);
      message.success(`已移除 ${name}`);
    },
    [adminIds, onChange]
  );

  const tryAddUser = useCallback(
    (user, { clearInput = true } = {}) => {
      const uid = Number(user?.id);
      if (!uid) {
        setAddTip('用户数据异常，请重试');
        setAddTipValid(false);
        return false;
      }
      if (uid === Number(landlordId)) {
        setAddTip('房东本人无需设为管理员');
        setAddTipValid(false);
        return false;
      }
      if (adminIds.includes(uid)) {
        setAddTip('该用户已是管理员');
        setAddTipValid(false);
        return false;
      }
      setAdmins((prev) => {
        if (prev.some((a) => Number(a.id) === uid)) return prev;
        return [...prev, user];
      });
      onChange?.([...adminIds, uid]);
      if (clearInput) setPhoneInput('');
      setAddTip(`已添加：${formatAdminDisplayName(user)}`);
      setAddTipValid(true);
      message.success('添加管理员成功');
      return true;
    },
    [adminIds, landlordId, onChange]
  );

  const filterAddableUsers = useCallback(
    (users) =>
      (users || []).filter((u) => {
        const uid = Number(u?.id);
        return uid > 0 && uid !== Number(landlordId) && !adminIds.includes(uid);
      }),
    [adminIds, landlordId]
  );

  const openUserPicker = useCallback((users) => {
    const addable = filterAddableUsers(users);
    if (addable.length === 0) {
      const hasLandlord = (users || []).some((u) => Number(u.id) === Number(landlordId));
      const hasDuplicate = (users || []).some((u) => adminIds.includes(Number(u.id)));
      if (hasLandlord && users.length === 1) {
        setAddTip('房东本人无需设为管理员');
      } else if (hasDuplicate && users.length === 1) {
        setAddTip('该用户已是管理员');
      } else {
        setAddTip('该手机号下的用户均不可添加（已是管理员或房东本人）');
      }
      setAddTipValid(false);
      return;
    }
    setPickerCandidates(addable);
    setSelectedCandidateId(Number(addable[0].id));
    setPickerOpen(true);
    setAddTip(`该手机号对应 ${users.length} 个微信账号，请选择要添加的用户`);
    setAddTipValid(false);
  }, [adminIds, filterAddableUsers, landlordId]);

  const handleConfirmPicker = useCallback(() => {
    const picked = pickerCandidates.find((u) => Number(u.id) === Number(selectedCandidateId));
    if (!picked) {
      message.warning('请选择要添加的用户');
      return;
    }
    const ok = tryAddUser(picked);
    if (ok) {
      setPickerOpen(false);
      setPickerCandidates([]);
      setSelectedCandidateId(null);
    }
  }, [pickerCandidates, selectedCandidateId, tryAddUser]);

  const handleAdd = useCallback(async () => {
    const phone = phoneInput.trim();
    if (phone.length !== 11) {
      setAddTip('请输入11位手机号');
      setAddTipValid(false);
      return;
    }
    setAdding(true);
    setAddTip('');
    try {
      const res = await getUsers({ page: 1, page_size: 100, keyword: phone });
      const users = res?.success && Array.isArray(res.data) ? res.data : [];
      const matched = users.filter((u) => String(u.phone || '').trim() === phone);
      if (matched.length === 0) {
        setAddTip('未找到该手机号对应的用户，请先让用户注册');
        setAddTipValid(false);
        return;
      }
      if (matched.length === 1) {
        tryAddUser(matched[0]);
        return;
      }
      openUserPicker(matched);
    } catch (err) {
      console.error('添加管理员失败', err);
      setAddTip('搜索用户失败，请重试');
      setAddTipValid(false);
    } finally {
      setAdding(false);
    }
  }, [phoneInput, tryAddUser, openUserPicker]);

  const showInitialLoading = loading && admins.length === 0 && adminIds.length > 0;

  return (
    <div style={styles.wrap}>
      <div style={styles.header}>
        <span>已设置 {adminIds.length} 位管理员</span>
        <span>房东本人无需添加</span>
      </div>

      {showInitialLoading ? (
        <div style={{ textAlign: 'center', padding: '16px 0' }}>
          <Spin size="small" />
        </div>
      ) : adminIds.length === 0 ? (
        <div style={styles.empty}>暂无管理员，可在下方输入手机号添加</div>
      ) : (
        <div style={styles.list}>
          {admins.map((admin) => (
            <div key={admin.id} style={styles.item}>
              <div style={styles.avatar}>
                <UserOutlined />
              </div>
              <div style={styles.info}>
                <div style={styles.name}>{admin.nickname || '未设置昵称'}</div>
                <div style={styles.sub}>{formatAdminSubline(admin)}</div>
              </div>
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined />}
                title="移除管理员"
                aria-label="移除管理员"
                style={styles.removeBtn}
                onClick={() => handleRemove(admin)}
              />
            </div>
          ))}
        </div>
      )}

      <div style={styles.addRow}>
        <Input
          placeholder="输入11位手机号"
          value={phoneInput}
          maxLength={11}
          onChange={(e) => {
            setPhoneInput(e.target.value.replace(/\D/g, '').slice(0, 11));
            if (addTip) setAddTip('');
          }}
          onPressEnter={handleAdd}
          style={{ flex: 1 }}
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          loading={adding}
          disabled={phoneInput.length !== 11}
          onClick={handleAdd}
        >
          添加
        </Button>
      </div>
      {addTip ? (
        <div
          style={{
            ...styles.tip,
            ...(addTipValid ? styles.tipValid : styles.tipInvalid),
          }}
        >
          {addTip}
        </div>
      ) : null}

      <Modal
        title="选择要添加的用户"
        open={pickerOpen}
        onCancel={() => {
          setPickerOpen(false);
          setPickerCandidates([]);
          setSelectedCandidateId(null);
        }}
        onOk={handleConfirmPicker}
        okText="确认添加"
        cancelText="取消"
        width={520}
        destroyOnHidden
      >
        <div style={{ marginBottom: 12, fontSize: 13, color: '#666' }}>
          该手机号绑定了多个微信账号，请选择其中一个设为管理员：
        </div>
        <Radio.Group
          value={selectedCandidateId}
          onChange={(e) => setSelectedCandidateId(e.target.value)}
          style={{ width: '100%' }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {pickerCandidates.map((u) => (
              <Radio key={u.id} value={Number(u.id)} style={{ alignItems: 'flex-start', margin: 0 }}>
                <div style={{ marginLeft: 4 }}>
                  <div style={{ fontWeight: 500, lineHeight: '20px' }}>
                    {formatAdminUserSelectLabel(u)}
                  </div>
                  <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                    {formatPickerSubline(u)}
                  </div>
                </div>
              </Radio>
            ))}
          </div>
        </Radio.Group>
      </Modal>
    </div>
  );
};

export default HouseAdminEditor;
